import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Button, Row } from "react-bootstrap";
import {
  container,
  accordionDetails,
  toggleButton,
  toggleIcon,
  contentContainer,
  horizontal,
  title,
  carouselContainer,
  carouselIcon,
  carouselPrevIcon,
  carouselNextIcon,
  horizantalDivider,
  time,
  divider,
  phoneContainer,
  addressContainer,
  descContainer,
  description,
  plainText,
  amenityList,
  amenitySeparator,
  amenityItem,
  aboutTitle,
  swipperContainer,
} from "./HotelSummary.module.scss";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper/types";
import fallbackImage from "../../../images/hotel_fallback.jpg";
import { StaticImage } from "gatsby-plugin-image";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhoneAlt } from "@fortawesome/free-solid-svg-icons";
import Col from "../../global/Col/Col";
import MetaHotelDetails from "../MetaHotelDetails/MetaHotelDetails";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { isMobile } from "react-device-detect";

export const Container: React.FC<any> = (props) => {
  return (
    <div
      className={`d-flex justify-content-center ${
        props.isMobile ? "flex-column" : ""
      }`}
    >
      <Row className={`g-0 pt-lg-4 pt-3 container-sm ${horizontal}`}>
        {props.children}
      </Row>
    </div>
  );
};

export const Title: React.FC<{
  id?: string;
  children: React.ReactNode;
}> = ({ id, children }) => {
  return (
    <div className={`${title}`} id={id}>
      {children}
    </div>
  );
};

export const Address: React.FC<{
  address?: {
    address_line1?: string;
    locality?: string;
    administrative_area?: string;
    postal_code?: string;
  };
}> = ({ address }) => {
  if (!address) {
    return null;
  }

  return (
    <span className={addressContainer}>
      {address?.address_line1}
      {address?.address_line1 && address?.locality && ", "}
      {address?.locality}
      {(address?.locality || address?.address_line1) &&
        address?.administrative_area &&
        ", "}
      {address?.administrative_area} {address?.postal_code}
    </span>
  );
};

export const AboutTitle: React.FC<{
  title: string;
}> = ({ title }) => {
  return (
    <h3 id="about-title" className={aboutTitle}>
      {title}
    </h3>
  );
};

export const Time: React.FC<{
  checkInTime?: string;
  checkOutTime?: string;
}> = ({ checkInTime, checkOutTime }) => {
  return (
    <div className={time}>
      <span id="checkin-time">Check-in: {checkInTime}</span>
      <div className={divider}></div>
      <span id="checkout-time">Checkout: {checkOutTime}</span>
    </div>
  );
};

export const Phone: React.FC<{ phone?: string }> = ({ phone }) => {
  if (!phone) return null;
  return (
    <div className={phoneContainer}>
      <div className={`mt-2 mb-2`}>
        <a href={`tel:${phone}`} style={{ color: "black" }} id="hotel-phone">
          <FontAwesomeIcon icon={faPhoneAlt} className="me-1" />
          <span>{phone}</span>
        </a>
      </div>
    </div>
  );
};

interface GalleryImage {
  imageUrl: string;
  altText: string;
  hotelName?: string;
}

interface HotelCarouselProps {
  galleryImages: GalleryImage[];
  isMobile: boolean;
  display?: string;
}

interface GalleryProps {
  galleryImages: GalleryImage[];
}

const Carousel: React.FC<GalleryProps> = ({ galleryImages }) => {
  const swiperRef = useRef<SwiperCore | null>(null);
  if (
    galleryImages.length === 1 &&
    galleryImages[0]?.imageUrl === fallbackImage
  ) {
    return (
      <img
        id="hotel-fallback-image"
        className="fallback-image"
        src={galleryImages[0]?.imageUrl}
        alt={galleryImages[0]?.hotelName}
      />
    );
  }
  return (
    <Swiper
      style={{
        "--swiper-pagination-color": "#fff",
        position: "relative",
        "min-width": "335px",
      }}
      lazy={true}
      className={swipperContainer}
      loop={true}
      slidesPerView={1}
      onBeforeInit={(swiper) => {
        swiperRef.current = swiper;
      }}
    >
      {galleryImages?.map((image) => (
        <SwiperSlide key={image.imageUrl}>
          <img
            loading="lazy"
            src={image.imageUrl}
            style={{
              width: "100%",
              height: "212px",
              borderRadius: "6px",
              objectFit: "cover",
            }}
            alt={image.altText}
          />
          <div className="swiper-lazy-preloader swiper-lazy-preloader-white"></div>
        </SwiperSlide>
      ))}
      <div className={carouselContainer}>
        <CarouselPrevIcon onClick={() => swiperRef.current?.slidePrev()} />
        <CarouselNextIcon onClick={() => swiperRef.current?.slideNext()} />
      </div>
    </Swiper>
  );
};

const handleKeyDown = (
  event: React.KeyboardEvent,
  onClick: () => void,
  triggerKey: string
) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    onClick();
  } else if (event.key === triggerKey) {
    onClick();
  }
};

export const CarouselPrevIcon: React.FC<{ onClick: () => void }> = ({
  onClick,
}) => (
  <div
    className={`${carouselIcon} ${carouselPrevIcon}`}
    onClick={onClick}
    tabIndex={0}
    role="button"
    aria-label="Previous image"
    onKeyDown={(event) => handleKeyDown(event, onClick, "ArrowLeft")}
  >
    <StaticImage
      src={"../../../svg/chevron_left.svg"}
      alt="hotel Card Carousel Left Button"
      placeholder="blurred"
      layout="fixed"
      formats={["png", "webp"]}
    />
  </div>
);

export const CarouselNextIcon: React.FC<{ onClick: () => void }> = ({
  onClick,
}) => (
  <div
    className={`${carouselIcon} ${carouselNextIcon}`}
    onClick={onClick}
    tabIndex={0}
    role="button"
    aria-label="Next image"
    onKeyDown={(event) => handleKeyDown(event, onClick, "ArrowRight")}
  >
    <StaticImage
      src={"../../../svg/chevron_right.svg"}
      alt="hotel Card Carousel Right Button"
      placeholder="blurred"
      layout="fixed"
      formats={["png", "webp"]}
    />
  </div>
);

export const HotelCarousel: React.FC<HotelCarouselProps> = ({
  galleryImages,
  isMobile,
  display,
}) => {
  return (
    <Carousel
      galleryImages={galleryImages}
      isMobile={isMobile}
      display={display}
    />
  );
};
interface AmenityListProps {
  amenities: { name: string }[]; // Define the structure of amenities if needed
}

export const DescContainer = (props: {
  children: React.ReactNode;
}): JSX.Element => {
  return <Col className={`${descContainer}`}>{props.children}</Col>;
};

export const Description = (props: {
  text: string;
  hotel: any;
  amenities: any;
}): JSX.Element => {
  const isMobile = useMediaQuery("(max-width: 991px)");
  const aboutText = props.text || "";

  // Function to remove all HTML tags and extract plain text
  const stripHtmlTags = (html: string): string => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html; // Parse the HTML string
    return tempDiv.textContent?.trim() || ""; // Extract and return the plain text
  };

  // Extract plain text from the input HTML
  const plainTextContent = stripHtmlTags(aboutText);

  return (
    <>
      <div id="description-container" className={`${description}`}>
        <p id="description-text" className={`${plainText}`}>
          {plainTextContent}
        </p>
      </div>
      {isMobile && props.amenities && props.amenities.length > 0 && (
        <>
          <AboutTitle title={"Includes"} />
          <AmenityList amenities={props.amenities} />
        </>
      )}
      <div id="meta-hotel-details">
        <MetaHotelDetails hotel={props.hotel} />
      </div>
    </>
  );
};

export const AccordionDetails = ({
  htmlContent,
  hotel,
  amenities,
  aboutTitle,
  checkInTime,
  checkOutTime,
  phone,
}: {
  htmlContent: string;
  hotel: any;
  amenities: any;
  aboutTitle: string;
  checkInTime: string;
  checkOutTime: string;
  phone: string;
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const isMobile = useMediaQuery("(max-width: 991px)");

  const handleToggleDetails = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowDetails((prev) => !prev);
  };

  return (
    <div id="accordion-details" className={`${accordionDetails}`}>
      {/* Toggle Button */}
      <button
        id="toggle-details-button" // Added ID for the button
        onClick={handleToggleDetails}
        aria-expanded={showDetails}
        aria-controls="details-content"
        aria-label={showDetails ? "Hide details" : "Show details"}
        className={toggleButton}
      >
        Hotel Summary
        <span
          id="toggle-icon" // Added ID for the icon container
          className={toggleIcon}
        >
          <FontAwesomeIcon
            id="chevron-icon" // Added ID for the FontAwesome icon
            className="fa-w-12"
            style={{ pointerEvents: "none" }}
            icon={showDetails ? "chevron-up" : "chevron-down"}
          />
        </span>
      </button>

      {/* Collapsible Content */}
      {showDetails && (
        <div id="details-content" className={`${contentContainer}`}>
          <DescContainer>
            {isMobile && (
              <>
                <AboutTitle title={aboutTitle} />
                <Time checkInTime={checkInTime} checkOutTime={checkOutTime} />
                <Phone phone={hotel?.phone} />
              </>
            )}
            <Description
              text={htmlContent}
              hotel={hotel}
              amenities={amenities}
            />
          </DescContainer>
        </div>
      )}
      {isMobile && (
        <div id="horizontal-divider" className={`${horizantalDivider}`}></div>
      )}
    </div>
  );
};

interface AmenityListProps {
  amenities: { name: string }[]; // Define the structure of amenities if needed
}

export const AmenityList: React.FC<AmenityListProps> = ({ amenities }) => {
  return (
    <div id="amenity-list" className={`${amenityList}`}>
      {amenities?.slice(0, 6)?.map((amenity, index) => (
        <React.Fragment key={amenity.name}>
          <span id={`amenity-item-${index}`} className={`${amenityItem}`}>
            <span
              id={`amenity-separator-${index}`}
              className={`${amenitySeparator}`}
            >
              •
            </span>
            {amenity.name}&nbsp;
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};
